import React from "react";
import { Row, Col, Space, Tooltip, Tag } from "antd";
import moment from "moment";
import { DeleteOutlined, CommentOutlined } from "@ant-design/icons";
import {
  FormItem,
  InputNumberItem,
  ButtonItem,
  SelectInsumosItem,
  DatePickerItem,
  dineroDisabledMask,
} from "./Items";
import InsumoDetalleRow from "./InsumoDetalleRow";

export const colorTagRequisicionOC = (importancia) => {
  switch (importancia) {
    case 3:
      return "red";
    case 2:
      return "orange";
    case 1:
      return "green";
    default:
      return "blue";
  }
};

/*
  Fila compartida de insumos para Agregar/Editar Orden de Compra.
  `fila` (insumosValues[arrayKey]) es la única prop cuya identidad determina si la
  fila necesita re-renderizarse (ver sonPropsIguales) — depende de que quien escriba
  en el store (aplicarFila/calcularCamposFila en las páginas) le dé una identidad
  nueva SOLO a la fila tocada, dejando las demás con la misma referencia.
*/
const FilaInsumoOrdenDeCompraBase = ({
  name,
  restField,
  arrayKey,
  remove,
  insumosValues,
  modo, // 'agregar' | 'editar'
  empresaId,
  headersToken,
  proveedorId,
  proyectoId,
  verInsumosComoSistema,
  mostrarDetalles,
  resaltado,
  totalColumnas,
  requisicionActiva = false,
  requisicionesAdicionales = [],
  recargarTrigger,
  escuela = false, // complemento Escuela: Desc. Prov. (descuentoBase) + Desc. Adic.
  funciones,
}) => {
  const fila = insumosValues?.[arrayKey] || {};
  const insumo = fila.insumoCompleto;
  const insumoId = fila.insumoId;

  const esEditar = modo === "editar";
  const disabledGeneral = esEditar && (fila.recibido || 0) > 0;
  const disabledCantidad =
    esEditar &&
    !!requisicionActiva &&
    !fila.cantidadRequisicion &&
    !fila.insumoSinRequisicion;
  const disabledSelect =
    (!!requisicionActiva && !fila.insumoSinRequisicion) || disabledGeneral;

  const combustible = !!insumo?.impuestos?.[0]?.combustible;
  const disabledPrecioConImpuestos = !combustible || disabledGeneral;

  let tagsRequisicion = [];
  if (esEditar) {
    const origenes =
      Array.isArray(fila.requisicionesOrigen) && fila.requisicionesOrigen.length > 0
        ? fila.requisicionesOrigen
        : fila.requisicionId
        ? [{ requisicionId: fila.requisicionId, cantidad: fila.cantidad }]
        : [];
    tagsRequisicion = origenes
      .map((origen) => ({
        origen,
        req: requisicionesAdicionales.find((r) => r._id === origen.requisicionId),
      }))
      .filter((x) => x.req)
      .map(({ req, origen }) => ({
        id: req._id,
        nombre: req.numero,
        fechaSolicitud: req.fechaSolicitud,
        importancia: req.importancia,
        cantidad: origen.cantidad,
      }));
  }

  let mostrarObservaciones = false;
  if (fila.insumoId && insumo) {
    mostrarObservaciones = insumo.observacionesVenta;
  }

  return (
    <React.Fragment>
      <tr
        id={`insumo-row-${arrayKey}`}
        style={
          resaltado
            ? { backgroundColor: "#fffbe6", outline: "2px solid #faad14", transition: "all 0.3s" }
            : {}
        }
      >
        <td className="td-border">
          <div style={{ fontSize: "14px", textAlign: "center", color: "#868585" }}>
            {arrayKey + 1}
          </div>
        </td>

        <td
          className="td-border"
          onDoubleClick={() => {
            if (!insumo) return;
            const idParaDisponibilidad = insumo?.insumoPrincipalId || insumo?._id;
            funciones.abrirDisponibilidad(idParaDisponibilidad, insumo?.nombre || insumo?.descripcion || "", proyectoId);
          }}
          style={{ cursor: "pointer" }}
        >
          <div style={{ fontSize: "14px", textAlign: "center", color: "#868585", pointerEvents: "none" }}>
            {insumo?.clave || "-"}
          </div>
        </td>

        <td className="td-border">
          <FormItem {...restField} name={[name, "insumoId"]} required noStyle>
            <SelectInsumosItem
              empresaId={empresaId}
              headersToken={headersToken}
              autoFocus
              sinBorde
              width="100%"
              dropdownMatchSelectWidth={600}
              placeholder="Insumo"
              tiposInsumos="ordenDeCompra"
              proveedorId={proveedorId}
              recargar={recargarTrigger}
              disabled={disabledSelect}
              insumoPreCargado={insumo}
              onChange={(value, insumoSeleccionado) => funciones.onChangeInsumo(value, arrayKey, insumoSeleccionado)}
              ocultarCodigoYUnidad
              usarSistema={verInsumosComoSistema}
            />
          </FormItem>
        </td>

        <td className="td-border">
          <div style={{ fontSize: "14px", textAlign: "center" }}>
            {insumo?.unidad || "-"}
          </div>
        </td>

        <td className="td-border">
          <FormItem {...restField} name={[name, "precioUnitario"]} noStyle required>
            <InputNumberItem
              sinBorde
              placeholder="Precio Unitario"
              onChange={(value) => funciones.onChangePrecioCantidadDescuento(value, arrayKey)}
              dinero
              disabled={disabledGeneral}
            />
          </FormItem>
        </td>

        <td className="td-border">
          <FormItem name={[name, "precioUnitarioConImpuestos"]} noStyle required>
            <InputNumberItem
              sinBorde
              disabled={disabledPrecioConImpuestos}
              placeholder="Precio Unit. c/Imp."
              onChange={(value) => funciones.onChangePrecioImpuestos(value, arrayKey)}
              dinero={!disabledPrecioConImpuestos}
              dineroDisabled={disabledPrecioConImpuestos}
            />
          </FormItem>
        </td>

        <td className="td-border">
          <FormItem {...restField} name={[name, "cantidad"]} required number noStyle type="number">
            <InputNumberItem
              sinBorde
              disabled={disabledCantidad || disabledGeneral}
              placeholder="Cantidad"
              onChange={(value) => funciones.onChangePrecioCantidadDescuento(value, arrayKey)}
            />
          </FormItem>
        </td>

        <td className="td-border">
          <FormItem {...restField} name={[name, "fechaEntrega"]} noStyle>
            <DatePickerItem sinBorde placeholder="Fecha de Entrega" disabled={disabledGeneral} />
          </FormItem>
        </td>

        {esEditar && (
          <td className="td-border" style={{ textAlign: "center" }}>
            {tagsRequisicion.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                {tagsRequisicion.map((t) => (
                  <Tooltip
                    key={t.id}
                    title={"Fecha de solicitud: " + (t.fechaSolicitud ? moment(t.fechaSolicitud).format("DD/MM/YYYY") : "")}
                  >
                    <Tag
                      color={colorTagRequisicionOC(t.importancia)}
                      onClick={() => funciones.verRequisicionModal(t.id)}
                      style={{ cursor: "pointer" }}
                    >
                      {"Req. " + t.nombre + (tagsRequisicion.length > 1 ? ` (${t.cantidad})` : "")}
                    </Tag>
                  </Tooltip>
                ))}
              </div>
            ) : null}
          </td>
        )}

        {escuela && (
          <td className="td-border">
            <FormItem {...restField} name={[name, "descuentoBase"]} numberCero noStyle initialValue={0}>
              <InputNumberItem
                sinBorde
                placeholder="Desc. Prov."
                porcentaje
                max={100}
                disabled={disabledGeneral}
                onChange={() => funciones.onChangeDescuentoEscuela(arrayKey)}
              />
            </FormItem>
          </td>
        )}

        <td className="td-border">
          <FormItem {...restField} name={[name, escuela ? "descuentoAdicional" : "descuento"]} required numberCero noStyle initialValue={0}>
            <InputNumberItem
              sinBorde
              placeholder={escuela ? "Desc. Adic." : "Descuento"}
              porcentaje
              max={100}
              disabled={disabledGeneral}
              onChange={(value) =>
                escuela
                  ? funciones.onChangeDescuentoEscuela(arrayKey)
                  : funciones.onChangePrecioCantidadDescuento(value, arrayKey)
              }
            />
          </FormItem>
        </td>

        <td className="td-border">
          <div className="celda-totales" style={{ fontSize: "12px" }}>
            <div>{dineroDisabledMask(fila.subtotal || 0)}</div>

            {combustible ? (
              <FormItem name={[name, "impuestos"]} required numberCero noStyle>
                <InputNumberItem
                  sinBorde
                  dinero
                  disabled={disabledGeneral}
                  placeholder="Impuestos"
                  onChange={(value) => funciones.onChangePrecioCantidadDescuento(value, arrayKey)}
                />
              </FormItem>
            ) : (
              <div>{dineroDisabledMask(fila.impuestos || 0)}</div>
            )}

            <div className="total-bold">Total: {dineroDisabledMask(fila.total || 0)}</div>
          </div>
          <FormItem {...restField} name={[name, "subtotal"]} required numberCero hidden><InputNumberItem /></FormItem>
          <FormItem {...restField} name={[name, "impuestos"]} required numberCero hidden><InputNumberItem /></FormItem>
          <FormItem {...restField} name={[name, "total"]} required numberCero hidden><InputNumberItem /></FormItem>
        </td>

        <td className="td-border-sin-right">
          <Row className="acciones-container">
            <Col span={24}>
              <Space
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
                size={4}
                className="acciones-container"
              >
                {mostrarObservaciones && (
                  <Tooltip title="Observaciones">
                    <ButtonItem
                      type="primary"
                      size="small"
                      icon={<CommentOutlined />}
                      verde={fila.observaciones}
                      onClick={() => funciones.abrirObservaciones(arrayKey, fila.observaciones, insumo?.observacionesPasadasProv)}
                    />
                  </Tooltip>
                )}

                {!disabledGeneral && (
                  <Tooltip title="Eliminar">
                    <ButtonItem
                      rojo2
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        remove(name);
                        funciones.cambiosInsumosOC?.();
                      }}
                    />
                  </Tooltip>
                )}
              </Space>
            </Col>
          </Row>
        </td>
      </tr>

      {mostrarDetalles && insumo && (
        <InsumoDetalleRow
          insumo={insumo}
          cantidad={fila.cantidad}
          costo={fila.precioUnitario}
          colSpans={{ details: 4, projection: 4, filler: escuela ? 1 : 0 }}
          onCloseModal={async () => {
            await funciones.recargarInsumoEditado(proveedorId, insumoId, arrayKey);
          }}
        />
      )}

      {fila.observaciones && (
        <tr className="fila-observaciones">
          <td colSpan={totalColumnas} className="td-border-sin-right">
            <div
              style={{
                display: "flex",
                justifyContent: "left",
                alignItems: "left",
                backgroundColor: fila.observacionesXml ? "#dceeffff" : "white",
              }}
            >
              <div style={{ width: "90%", textAlign: "left", paddingLeft: 20 }}>
                {String(fila.observaciones)
                  .split(/\r?\n/)
                  .map((line, i) => (
                    <span key={i}>
                      {line}
                      <br />
                    </span>
                  ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

// Única fuente de verdad de si la fila necesita re-renderizarse: la identidad de
// `fila` (insumosValues[arrayKey]). Las páginas garantizan (vía aplicarFila/
// calcularCamposFila) que solo la fila tocada recibe una identidad nueva en cada
// recálculo — el resto conserva su referencia y por lo tanto no re-renderiza aquí.
// `remove`/`restField` quedan fuera a propósito: cambian de referencia en cada
// render de Form.List pero su comportamiento para un `name` dado no cambia.
const sonPropsIguales = (prev, next) => {
  return (
    prev.arrayKey === next.arrayKey &&
    prev.name === next.name &&
    prev.insumosValues?.[prev.arrayKey] === next.insumosValues?.[next.arrayKey] &&
    prev.modo === next.modo &&
    prev.empresaId === next.empresaId &&
    prev.headersToken === next.headersToken &&
    prev.proveedorId === next.proveedorId &&
    prev.proyectoId === next.proyectoId &&
    prev.verInsumosComoSistema === next.verInsumosComoSistema &&
    prev.mostrarDetalles === next.mostrarDetalles &&
    prev.resaltado === next.resaltado &&
    prev.totalColumnas === next.totalColumnas &&
    prev.requisicionActiva === next.requisicionActiva &&
    prev.requisicionesAdicionales === next.requisicionesAdicionales &&
    prev.recargarTrigger === next.recargarTrigger &&
    prev.escuela === next.escuela &&
    prev.funciones === next.funciones
  );
};

const FilaInsumoOrdenDeCompra = React.memo(FilaInsumoOrdenDeCompraBase, sonPropsIguales);

export default FilaInsumoOrdenDeCompra;
